import { useCallback, useEffect, useRef, useState } from 'react';
import { buildApiUrl } from '../utils/serverOrigin';

/* ─── UTM helpers ─── */
function getUtmSource(): string | undefined {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('utm_source') || undefined;
  } catch {
    return undefined;
  }
}

/* ─── Design variant type ─── */
export type DesignVariant = 'A' | 'B' | 'C';

/* ─── Component ─── */
export default function WaitlistLanding({ variant = 'A' }: { variant?: DesignVariant }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [count, setCount] = useState<number | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Fetch waitlist count
  useEffect(() => {
    fetch(buildApiUrl('/api/waitlist/count'))
      .then((r) => r.json())
      .then((data) => setCount(data.count ?? 0))
      .catch(() => setCount(0));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim() || status === 'submitting') return;
      setStatus('submitting');
      try {
        const res = await fetch(buildApiUrl('/api/waitlist/join'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), utmSource: getUtmSource() }),
        });
        const data = await res.json();
        if (!res.ok) {
          setStatus('error');
          setMessage(data.error || 'Something went wrong');
          return;
        }
        setStatus('success');
        setMessage(data.message || "You're in!");
        // Refresh counter
        fetch(buildApiUrl('/api/waitlist/count'))
          .then((r) => r.json())
          .then((d) => setCount(d.count ?? 0))
          .catch(() => {});
      } catch {
        setStatus('error');
        setMessage('Network error — please try again');
      }
    },
    [email, status],
  );

  /* ─── Shared data ─── */
  const counterDisplay = count !== null ? count : '—';
  const dmgUrl = 'https://github.com/clawlet-ai/teams/releases/latest';

  const roadmapItems = [
    { icon: '✅', label: 'macOS app', detail: 'Available now (free)', done: true },
    { icon: '📱', label: 'iOS + cloud sync', detail: 'Q2 2026', done: false },
    { icon: '🤖', label: 'Android', detail: 'Q3 2026', done: false },
    { icon: '☁️', label: 'Cloud-hosted', detail: 'v1.0', done: false },
  ];

  /* ══════════════════════════════════════════════════
     DESIGN A — "Midnight Aurora"
     Bold dark theme with animated aurora gradients.
     Large typography. Floating glass cards.
     Purple / blue / teal color palette.
     ══════════════════════════════════════════════════ */
  if (variant === 'A') {
    return (
      <div className="wl-root wl-a">
        {/* Aurora animated background */}
        <div className="wl-aurora-bg" />

        {/* ─── NAV ─── */}
        <nav className="wl-nav">
          <div className="wl-nav-inner">
            <span className="wl-logo">
              <span className="wl-logo-gem">✦</span>
              <span className="wl-logo-text">clawlet</span>
            </span>
            <a href={dmgUrl} className="wl-nav-cta" target="_blank" rel="noopener noreferrer">
              Download for Mac
            </a>
          </div>
        </nav>

        {/* ─── HERO ─── */}
        <section className="wl-hero">
          <div className="wl-hero-inner">
            <div className="wl-eyebrow">
              <span className="wl-eyebrow-dot" />
              Free on macOS
            </div>
            <h1 className="wl-h1">
              The interface for<br />
              <span className="wl-accent">AI agents.</span>
            </h1>
            <p className="wl-sub">
              See Claude's thinking tokens, tool calls, and subagent activity stream in real-time.
              No more blank screens. Free forever on macOS.
            </p>
            <div className="wl-hero-actions">
              <a href={dmgUrl} className="wl-btn-primary" target="_blank" rel="noopener noreferrer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Free macOS .dmg
              </a>
              <a href="#waitlist" className="wl-btn-ghost">
                Join waitlist →
              </a>
            </div>

            {/* Demo placeholder */}
            <div className="wl-demo-placeholder">
              <div className="wl-demo-frame">
                <div className="wl-demo-titlebar">
                  <span className="wl-dot wl-dot-r" />
                  <span className="wl-dot wl-dot-y" />
                  <span className="wl-dot wl-dot-g" />
                  <span className="wl-demo-label">clawlet — agent stream</span>
                </div>
                <div className="wl-demo-body">
                  <div className="wl-demo-stream">
                    <div><span className="wl-stream-thinking">◈ thinking</span> &nbsp;analyzing repository structure...</div>
                    <div className="wl-stream-dim">  token budget: 8,192 · elapsed: 2.3s</div>
                    <div><span className="wl-stream-tool">⟩ tool_call</span> &nbsp;read_file("src/main.rs")</div>
                    <div className="wl-stream-dim">  → 247 lines · 8.4 KB</div>
                    <div><span className="wl-stream-tool">⟩ tool_call</span> &nbsp;search_code(pattern="fn handle")</div>
                    <div><span className="wl-stream-result">✓ result</span> &nbsp;found 12 matches across 4 files</div>
                    <div className="wl-stream-dim">  subagent spawned: code-reviewer · session 0x4f2a</div>
                  </div>
                  <div className="wl-demo-gif-hint">[ Demo GIF coming soon ]</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── WAITLIST ─── */}
        <section className="wl-waitlist" id="waitlist">
          <div className="wl-waitlist-inner">
            <h2 className="wl-h2">
              Cloud sync ships Q2 2026.{' '}
              <span className="wl-accent">Get 50% off lifetime.</span>
            </h2>
            <p className="wl-waitlist-sub">First 1,000 spots only — no spam, ever.</p>

            {status === 'success' ? (
              <div className="wl-success-box">
                <span className="wl-success-icon">✓</span>
                <span>{message || "You're on the list!"}</span>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="wl-form">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="wl-input"
                  disabled={status === 'submitting'}
                />
                <button
                  type="submit"
                  className="wl-btn-submit"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Joining…' : 'Join Waitlist'}
                </button>
              </form>
            )}
            {status === 'error' && <p className="wl-error">{message}</p>}

            <div className="wl-counter">
              <div className="wl-counter-bar">
                <div
                  className="wl-counter-fill"
                  style={{ width: `${Math.min(((count ?? 0) / 1000) * 100, 100)}%` }}
                />
              </div>
              <span className="wl-counter-text">
                <span className="wl-counter-num">{counterDisplay}</span> / 1,000 lifetime spots claimed
              </span>
            </div>
          </div>
        </section>

        {/* ─── ROADMAP ─── */}
        <section className="wl-roadmap">
          <div className="wl-roadmap-inner">
            <h2 className="wl-h2">Roadmap</h2>
            <div className="wl-roadmap-grid">
              {roadmapItems.map((item, i) => (
                <div key={i} className={`wl-roadmap-card${item.done ? ' wl-done' : ''}`}>
                  <span className="wl-rc-icon">{item.icon}</span>
                  <span className="wl-rc-label">{item.label}</span>
                  <span className="wl-rc-detail">{item.detail}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ─── FOOTER ─── */}
        <footer className="wl-footer">
          <div className="wl-footer-inner">
            <a href="https://github.com/clawlet-ai/teams" className="wl-footer-link" target="_blank" rel="noopener noreferrer">GitHub</a>
            <span className="wl-footer-sep">·</span>
            <span>Built by Jackie &amp; friends in Tokyo</span>
          </div>
        </footer>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     DESIGN B — "Signal Fire"
     High-energy, developer-focused.
     Orange/amber on dark. VS Code + Stripe mashup.
     ══════════════════════════════════════════════════ */
  if (variant === 'B') {
    return (
      <div className="wl-root wl-b">
        {/* Background grid + glow */}
        <div className="wl-grid-texture" />
        <div className="wl-signal-glow" />

        {/* ─── NAV ─── */}
        <nav className="wl-nav">
          <div className="wl-nav-inner">
            <span className="wl-logo">
              <span className="wl-logo-bracket">{'</>'}</span>
              <span className="wl-logo-text">clawlet</span>
            </span>
            <div className="wl-nav-pill">
              <a href={dmgUrl} className="wl-nav-cta" target="_blank" rel="noopener noreferrer">
                Download .dmg
              </a>
            </div>
          </div>
        </nav>

        {/* ─── HERO ─── */}
        <section className="wl-hero">
          <div className="wl-hero-inner">
            <div className="wl-code-tag">
              <span className="wl-code-tag-kw">const</span>{' '}
              status{' '}
              <span className="wl-code-tag-kw">=</span>{' '}
              <span className="wl-code-tag-str">"free on macOS"</span>
            </div>
            <h1 className="wl-h1">
              The interface for{' '}
              <span className="wl-accent-under">
                <span className="wl-accent">AI agents.</span>
              </span>
            </h1>
            <p className="wl-sub">
              See thinking tokens, tool calls, and subagent streams in real-time.
              Built for developers who care about what's happening under the hood.
            </p>
            <div className="wl-hero-actions">
              <a href={dmgUrl} className="wl-btn-primary" target="_blank" rel="noopener noreferrer">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Download for macOS
              </a>
              <a href="#waitlist" className="wl-btn-ghost">
                Join waitlist →
              </a>
            </div>

            {/* Demo — VS Code-style panel */}
            <div className="wl-demo-placeholder">
              <div className="wl-demo-outer">
                <div className="wl-demo-titlebar">
                  <span className="wl-demo-tab wl-active">agent-stream.ts</span>
                  <span className="wl-demo-tab wl-inactive">tools.ts</span>
                  <span className="wl-demo-tab wl-inactive">thinking.log</span>
                </div>
                <div className="wl-demo-body">
                  <div className="wl-demo-gutter">
                    <span>1</span><span>2</span><span>3</span><span>4</span>
                    <span>5</span><span>6</span><span>7</span><span>8</span>
                  </div>
                  <div className="wl-demo-code">
                    <div>
                      <span className="wl-code-kw">const</span>{' '}
                      <span className="wl-code-var">stream</span>{' '}
                      <span className="wl-code-op">=</span>{' '}
                      <span className="wl-code-kw">await</span>{' '}
                      <span className="wl-code-fn">agent</span>
                      <span className="wl-code-op">.</span>
                      <span className="wl-code-fn">stream</span>
                      <span className="wl-code-op">({'{}'})</span>
                    </div>
                    <div>
                      <span className="wl-code-cmt">// thinking tokens — real-time</span>
                    </div>
                    <div>
                      <span className="wl-code-kw">for await</span>{' '}
                      (<span className="wl-code-kw">const</span>{' '}
                      <span className="wl-code-var">chunk</span>{' '}
                      <span className="wl-code-kw">of</span>{' '}
                      <span className="wl-code-var">stream</span>
                      <span className="wl-code-op">.</span>
                      <span className="wl-code-fn">thinking</span>()) {'{'}
                    </div>
                    <div>&nbsp;&nbsp;<span className="wl-code-fn">display</span>(<span className="wl-code-var">chunk</span><span className="wl-code-op">.</span><span className="wl-code-var">text</span>)</div>
                    <div>{'}'}</div>
                    <div>
                      <span className="wl-code-cmt">// tool calls — live trace</span>
                    </div>
                    <div>
                      <span className="wl-code-var">stream</span>
                      <span className="wl-code-op">.</span>
                      <span className="wl-code-fn">onToolCall</span>(<span className="wl-code-var">renderCall</span>)
                    </div>
                    <div>
                      <span className="wl-code-cmt">// Demo GIF coming soon</span>
                    </div>
                  </div>
                </div>
                <div className="wl-demo-statusbar">
                  <span>TypeScript</span>
                  <span className="wl-status-sep">|</span>
                  <span>UTF-8</span>
                  <span className="wl-status-sep">|</span>
                  <span>agent-stream: active</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ─── WAITLIST ─── */}
        <section className="wl-waitlist" id="waitlist">
          <div className="wl-waitlist-inner">
            <h2 className="wl-h2">
              Lock in 50%{' '}
              <span className="wl-accent">lifetime discount.</span>
            </h2>
            <p className="wl-waitlist-sub">Cloud sync ships Q2 2026. First 1,000 spots only.</p>

            {status === 'success' ? (
              <div className="wl-success-box">
                <span>✓ {message || "You're on the list!"}</span>
              </div>
            ) : (
              <form ref={formRef} onSubmit={handleSubmit} className="wl-form">
                <span className="wl-input-prefix">$</span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@domain.com"
                  required
                  className="wl-input"
                  disabled={status === 'submitting'}
                />
                <button type="submit" className="wl-btn-submit" disabled={status === 'submitting'}>
                  {status === 'submitting' ? 'Joining…' : 'Join Waitlist'}
                </button>
              </form>
            )}
            {status === 'error' && <p className="wl-error">// error: {message}</p>}

            <div className="wl-counter">
              <span className="wl-counter-badge">{counterDisplay}</span>
              <span>/ 1,000 lifetime spots claimed</span>
            </div>
          </div>
        </section>

        {/* ─── ROADMAP ─── */}
        <section className="wl-roadmap">
          <div className="wl-roadmap-inner">
            <h2 className="wl-h2">Roadmap</h2>
            <ul className="wl-timeline">
              {roadmapItems.map((item, i) => (
                <li key={i} className={`wl-timeline-item${item.done ? ' wl-done' : ''}`}>
                  <span className="wl-tl-status">{item.done ? 'SHIPPED' : 'PLANNED'}</span>
                  <span className="wl-tl-label">{item.label}</span>
                  <span className="wl-tl-detail">{item.detail}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ─── FOOTER ─── */}
        <footer className="wl-footer">
          <div className="wl-footer-inner">
            <a href="https://github.com/clawlet-ai/teams" className="wl-footer-link" target="_blank" rel="noopener noreferrer">github</a>
            <span className="wl-footer-sep">|</span>
            <span>built by Jackie &amp; friends in Tokyo</span>
          </div>
        </footer>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════
     DESIGN C — "Crystal Clear"
     Clean light theme. White/cream with depth.
     Sharp typography. Strategic color pops.
     Think Linear.app or Vercel landing.
     ══════════════════════════════════════════════════ */
  return (
    <div className="wl-root wl-c">
      {/* Decorative background accents */}
      <div className="wl-bg-accents" />

      {/* ─── NAV ─── */}
      <nav className="wl-nav">
        <div className="wl-nav-inner">
          <span className="wl-logo">
            <span className="wl-logo-mark">⚡</span>
            <span className="wl-logo-text">Clawlet</span>
          </span>
          <div className="wl-nav-right">
            <a href="#waitlist" className="wl-nav-ghost">Join waitlist</a>
            <a href={dmgUrl} className="wl-nav-cta" target="_blank" rel="noopener noreferrer">
              Download
            </a>
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="wl-hero">
        <div className="wl-hero-inner">
          <a href={dmgUrl} className="wl-badge" target="_blank" rel="noopener noreferrer">
            Free on macOS — download now
            <span className="wl-badge-arrow">→</span>
          </a>
          <h1 className="wl-h1">
            The interface for<br />
            <span className="wl-accent">AI agents.</span>
          </h1>
          <p className="wl-sub">
            See Claude's thinking tokens, tool calls, and subagent activity
            stream in real-time. No more blank screens.
          </p>
          <div className="wl-hero-actions">
            <a href={dmgUrl} className="wl-btn-primary" target="_blank" rel="noopener noreferrer">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Free macOS .dmg
            </a>
            <a href="#waitlist" className="wl-btn-ghost">
              Join waitlist →
            </a>
          </div>

          {/* Feature chips */}
          <div className="wl-chips">
            <span className="wl-chip">
              <span className="wl-chip-dot wl-chip-dot-purple" />
              Thinking tokens
            </span>
            <span className="wl-chip">
              <span className="wl-chip-dot wl-chip-dot-teal" />
              Tool call traces
            </span>
            <span className="wl-chip">
              <span className="wl-chip-dot wl-chip-dot-blue" />
              Real-time streaming
            </span>
          </div>

          {/* Demo placeholder */}
          <div className="wl-demo-placeholder" style={{ marginTop: '48px' }}>
            <div className="wl-demo-outer">
              <div className="wl-demo-titlebar">
                <span className="wl-dot wl-dot-r" />
                <span className="wl-dot wl-dot-y" />
                <span className="wl-dot wl-dot-g" />
                <span className="wl-demo-title-text">Clawlet — Agent Stream</span>
              </div>
              <div className="wl-demo-body">
                <div className="wl-demo-icon">🤖</div>
                <div className="wl-demo-hint">Demo GIF coming soon</div>
                <div className="wl-demo-hint-sub">Watch thinking tokens, tool calls, and subagent streams live</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WAITLIST ─── */}
      <section className="wl-waitlist" id="waitlist">
        <div className="wl-waitlist-inner">
          <h2 className="wl-h2">
            Get early access.
          </h2>
          <p className="wl-waitlist-sub">
            Cloud sync launches Q2 2026. First 1,000 signups get 50% off lifetime — no spam, unsubscribe anytime.
          </p>

          {status === 'success' ? (
            <div className="wl-success-box">
              <div className="wl-success-icon-wrap">✓</div>
              <p>{message || "You're on the list!"}</p>
            </div>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit} className="wl-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="wl-input"
                disabled={status === 'submitting'}
              />
              <button type="submit" className="wl-btn-submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Joining…' : 'Join Waitlist'}
              </button>
            </form>
          )}
          {status === 'error' && <p className="wl-error">{message}</p>}

          <div className="wl-counter">
            <div className="wl-counter-bar">
              <div
                className="wl-counter-fill"
                style={{ width: `${Math.min(((count ?? 0) / 1000) * 100, 100)}%` }}
              />
            </div>
            <span className="wl-counter-text">
              <span className="wl-counter-num">{counterDisplay}</span> / 1,000 lifetime spots claimed
            </span>
          </div>
        </div>
      </section>

      {/* ─── ROADMAP ─── */}
      <section className="wl-roadmap">
        <div className="wl-roadmap-inner">
          <h2 className="wl-h2">Roadmap</h2>
          <p className="wl-roadmap-sub">Where we're headed — in order.</p>
          <div className="wl-roadmap-grid">
            {roadmapItems.map((item, i) => (
              <div key={i} className={`wl-roadmap-card${item.done ? ' wl-done' : ''}`}>
                <span className="wl-rc-icon">{item.icon}</span>
                <span className="wl-rc-label">{item.label}</span>
                <span className="wl-rc-detail">{item.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="wl-footer">
        <div className="wl-footer-inner">
          <a href="https://github.com/clawlet-ai/teams" className="wl-footer-link" target="_blank" rel="noopener noreferrer">GitHub</a>
          <span className="wl-footer-sep">·</span>
          <span>Built by Jackie &amp; friends in Tokyo</span>
        </div>
      </footer>
    </div>
  );
}
